import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, PenLine, Send } from "lucide-react";
import { toast } from "sonner";

import { publicationErrorMessage } from "@/lib/publication-status";
import { z } from "zod";

import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Checkbox } from "@/components/ui/checkbox";
import { PublicationCoverField } from "@/components/publication-cover-field";
import {
  fetchCategories,
  fetchChapters,
  fetchContentTypes,
  fetchMyPenNames,
  fetchMyStories,
} from "@/lib/queries";
import { cn } from "@/lib/utils";
import { TagInput } from "@/components/tag-input";
import {
  fetchStoryTags,
  MAX_TAGS_PER_PUBLICATION,
  saveStoryTags,
  TagValidationError,
  type TagSummary,
} from "@/lib/tags";

export const Route = createFileRoute("/_authenticated/write")({
  head: () => ({
    meta: [
      { title: "Create a publication — Quibblies" },
      {
        name: "description",
        content:
          "Draft, edit and submit your writing — fiction, personal experience, reflection or poetry — for moderation review before it publishes on Quibblies.",
      },
      { property: "og:title", content: "Create a publication — Quibblies" },
      { property: "og:description", content: "Draft and submit your writing on Quibblies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WritePage,
});

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under review",
  changes_requested: "Changes requested",
  rejected: "Rejected",
  approved: "Approved",
  published: "Published",
  removed: "Removed",
};

const storySchema = z.object({
  title: z.string().trim().min(2, "Give your story a title").max(120),
  description: z.string().trim().max(600).optional(),
  content: z.string().trim().min(50, "Write at least a few paragraphs before saving"),
});

const STORY_TYPES = [
  { value: "short_story", label: "Short story" },
  { value: "long_story", label: "Long story" },
  { value: "book", label: "Book" },
  { value: "series", label: "Series" },
] as const;

function countWords(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function WritePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    title: "",
    description: "",
    content: "",
    storyType: "short_story",
    categoryId: "",
    contentTypeId: "",
    penNameId: "",
  });
  const [privacyAck, setPrivacyAck] = React.useState(false);
  const [tags, setTags] = React.useState<TagSummary[]>([]);
  const [tagError, setTagError] = React.useState<string | null>(null);
  const [newPenName, setNewPenName] = React.useState("");

  const stories = useQuery({
    queryKey: ["my-stories", user?.id],
    queryFn: () => fetchMyStories(user!.id),
    enabled: Boolean(user),
  });
  const categories = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const contentTypes = useQuery({ queryKey: ["content-types"], queryFn: fetchContentTypes });
  const penNames = useQuery({
    queryKey: ["pen-names", user?.id],
    queryFn: () => fetchMyPenNames(user!.id),
    enabled: Boolean(user),
  });

  const selected = (stories.data ?? []).find((story) => story.id === selectedId) ?? null;

  const storyTagsQuery = useQuery({
    queryKey: ["story-tags", selectedId],
    queryFn: () => fetchStoryTags(selectedId!),
    enabled: Boolean(selectedId),
  });

  const chapters = useQuery({
    queryKey: ["chapters", selectedId],
    queryFn: () => fetchChapters(selectedId!),
    enabled: Boolean(selectedId),
  });

  React.useEffect(() => {
    if (!selected) return;
    setForm((prev) => ({
      ...prev,
      title: selected.title,
      description: selected.description ?? "",
      storyType: selected.story_type,
      contentTypeId: selected.content_type_id ?? "",
      penNameId: selected.pen_name_id ?? "",
    }));
    setPrivacyAck(Boolean(selected.privacy_acknowledged_at));
  }, [selected]);

  React.useEffect(() => {
    if (storyTagsQuery.data) setTags(storyTagsQuery.data);
  }, [storyTagsQuery.data]);

  React.useEffect(() => {
    const first = chapters.data?.[0];
    if (first) setForm((prev) => ({ ...prev, content: first.content }));
  }, [chapters.data]);

  const activeContentType = (contentTypes.data ?? []).find(
    (type) => type.id === form.contentTypeId,
  );
  const isPersonal = Boolean(activeContentType?.is_personal);

  const addPenName = useMutation({
    mutationFn: async () => {
      const name = newPenName.trim();
      if (name.length < 2) throw new Error("Pen names need at least 2 characters");
      const { data, error } = await supabase
        .from("pen_names")
        .insert({ user_id: user!.id, name })
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: (id) => {
      setNewPenName("");
      setForm((prev) => ({ ...prev, penNameId: id }));
      void queryClient.invalidateQueries({ queryKey: ["pen-names"] });
      toast.success("Pen name added");
    },
    onError: (error: Error) => toast.error(publicationErrorMessage(error)),
  });

  const editable = !selected || ["draft", "changes_requested", "rejected"].includes(selected.status);

  function startNew() {
    setSelectedId(null);
    setForm({
      title: "",
      description: "",
      content: "",
      storyType: "short_story",
      categoryId: "",
      contentTypeId: "",
      penNameId: "",
    });
    setPrivacyAck(false);
    setTags([]);
  }

  const save = useMutation({
    mutationFn: async () => {
      const parsed = storySchema.safeParse({
        title: form.title,
        description: form.description,
        content: form.content,
      });
      if (!parsed.success) throw new Error(parsed.error.issues[0]!.message);
      if (!form.contentTypeId) throw new Error("Choose a content type for this publication");
      if (isPersonal && !privacyAck) throw new Error("Confirm the privacy acknowledgement first");

      const words = countWords(parsed.data.content);
      const payload = {
        title: parsed.data.title,
        description: parsed.data.description || null,
        story_type: form.storyType as "short_story",
        content_type_id: form.contentTypeId,
        pen_name_id: form.penNameId || null,
        privacy_acknowledged_at: privacyAck ? new Date().toISOString() : null,
        category_id: form.categoryId || null,
        word_count: words,
        reading_minutes: Math.max(1, Math.round(words / 220)),
      };

      let storyId = selectedId;
      if (storyId) {
        const { error } = await supabase.from("stories").update(payload).eq("id", storyId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("stories")
          .insert({ ...payload, author_id: user!.id, status: "draft" })
          .select("id")
          .single();
        if (error) throw error;
        storyId = data.id;
      }

      const existing = chapters.data?.[0];
      if (existing && selectedId) {
        const { error } = await supabase
          .from("chapters")
          .update({ content: parsed.data.content, word_count: words, title: parsed.data.title })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("chapters").insert({
          story_id: storyId!,
          position: 1,
          title: parsed.data.title,
          content: parsed.data.content,
          word_count: words,
        });
        if (error) throw error;
      }

      // Tags live in their own table and go through server-side validation
      // (approved tags only, max 15, author-owned, editable states only).
      setTagError(null);
      try {
        await saveStoryTags(storyId!, tags.map((tag) => tag.id));
      } catch (error) {
        if (error instanceof TagValidationError) {
          setTagError(error.message);
        }
        throw error;
      }

      return storyId!;
    },
    onSuccess: (storyId) => {
      setSelectedId(storyId);
      void queryClient.invalidateQueries({ queryKey: ["my-stories"] });
      void queryClient.invalidateQueries({ queryKey: ["chapters"] });
      void queryClient.invalidateQueries({ queryKey: ["story-tags"] });
      toast.success("Draft saved");
    },
    onError: (error: Error) => toast.error(publicationErrorMessage(error)),
  });

  const submitForReview = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("Save your draft first");
      if (isPersonal && !privacyAck) throw new Error("Confirm the privacy acknowledgement first");
      const words = countWords(form.content);

      const { data: version, error: versionError } = await supabase
        .from("story_versions")
        .insert({
          story_id: selectedId,
          version_number: 1,
          word_count: words,
          snapshot: {
            title: form.title,
            description: form.description,
            content: form.content,
          },
        })
        .select("id")
        .single();
      if (versionError) throw versionError;

      const { error: submissionError } = await supabase.from("submissions").insert({
        story_id: selectedId,
        version_id: version.id,
        author_id: user!.id,
        status: "submitted",
      });
      if (submissionError) throw submissionError;

      const { error: storyError } = await supabase
        .from("stories")
        .update({ status: "submitted" })
        .eq("id", selectedId);
      if (storyError) throw storyError;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-stories"] });
      toast.success("Sent to moderation — you'll be notified when it's reviewed.");
    },
    onError: (error: Error) => toast.error(publicationErrorMessage(error)),
  });

  return (
    <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[280px_1fr]">
      <aside>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg text-foreground">Your publications</h2>
          <Button size="sm" variant="soft" onClick={startNew}>
            New
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {stories.isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-16 rounded-xl" />
            ))
          ) : (stories.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No drafts yet. Start writing →</p>
          ) : (
            stories.data!.map((story) => (
              <button
                key={story.id}
                type="button"
                onClick={() => setSelectedId(story.id)}
                className={cn(
                  "w-full rounded-xl border border-border/70 bg-surface/50 p-3 text-left transition-smooth hover:border-primary/50",
                  selectedId === story.id && "border-primary/60 bg-accent",
                )}
              >
                <span className="line-clamp-1 text-sm text-foreground">{story.title}</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {STATUS_LABELS[story.status] ?? story.status} · {story.word_count} words
                </span>
              </button>
            ))
          )}
        </div>
      </aside>

      <section>
        <h1 className="font-display text-4xl text-foreground">
          {selectedId ? "Edit publication" : "Create publication"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Drafts stay private. Submitting sends your publication to moderation review — nothing publishes
          automatically.
        </p>

        {selected && !editable ? (
          <div className="mt-6 rounded-2xl border border-primary/40 bg-accent/60 p-4 text-sm text-muted-foreground">
            This publication is {STATUS_LABELS[selected.status]?.toLowerCase()} and is locked for editing.
          </div>
        ) : null}

        <div className="mt-8 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={form.title}
              disabled={!editable}
              maxLength={120}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Blurb</Label>
            <Textarea
              id="description"
              rows={3}
              maxLength={600}
              disabled={!editable}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="One or two sentences to hook a reader."
            />
          </div>

          <PublicationCoverField
            storyId={selectedId}
            coverUrl={selected?.cover_url ?? null}
            disabled={!editable}
          />



          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Content type</Label>
              <Select
                value={form.contentTypeId}
                disabled={!editable}
                onValueChange={(value) => setForm((prev) => ({ ...prev, contentTypeId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="What kind of writing is this?" />
                </SelectTrigger>
                <SelectContent>
                  {(contentTypes.data ?? []).map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeContentType?.description ? (
                <p className="text-xs text-muted-foreground">{activeContentType.description}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Publish as</Label>
              <Select
                value={form.penNameId || "__account"}
                disabled={!editable}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, penNameId: value === "__account" ? "" : value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__account">My profile name</SelectItem>
                  {(penNames.data ?? []).map((pen) => (
                    <SelectItem key={pen.id} value={pen.id}>
                      {pen.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input
                  value={newPenName}
                  maxLength={40}
                  disabled={!editable}
                  placeholder="Add a pen name"
                  onChange={(event) => setNewPenName(event.target.value)}
                />
                <Button
                  type="button"
                  variant="soft"
                  size="sm"
                  disabled={!editable || !newPenName.trim() || addPenName.isPending}
                  onClick={() => addPenName.mutate()}
                >
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Your legal name, email, date of birth and account details never appear publicly.
              </p>
            </div>
          </div>

          {isPersonal ? (
            <div className="space-y-3 rounded-2xl border border-primary/40 bg-accent/50 p-5 text-sm text-muted-foreground">
              <p className="text-foreground">
                Please protect your privacy and the privacy of other people. Avoid publishing
                identifying personal information without permission.
              </p>
              <p>
                Change or omit full names, addresses, phone numbers, email addresses, workplaces,
                schools, exact locations, social media accounts and other identifying details.
              </p>
              <label className="flex items-start gap-3 text-foreground">
                <Checkbox
                  checked={privacyAck}
                  disabled={!editable}
                  onCheckedChange={(value) => setPrivacyAck(value === true)}
                  className="mt-0.5"
                />
                <span>
                  I understand that I am responsible for protecting my privacy and the privacy of
                  people mentioned in my writing.
                </span>
              </label>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Format</Label>
              <Select
                value={form.storyType}
                disabled={!editable}
                onValueChange={(value) => setForm((prev) => ({ ...prev, storyType: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STORY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.categoryId}
                disabled={!editable}
                onValueChange={(value) => setForm((prev) => ({ ...prev, categoryId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {(categories.data ?? []).map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-surface/40 p-4">
            <TagInput
              value={tags}
              onChange={(next) => {
                setTagError(null);
                setTags(next);
              }}
              disabled={!editable}
              serverError={tagError}
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Up to {MAX_TAGS_PER_PUBLICATION} hashtags. Hashtags help readers discover your
              writing. They sit alongside your content type —
              they never replace it. Changing significant tags after publishing goes back through
              moderation.
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="content">Your writing</Label>
              <span className="text-xs text-muted-foreground">
                {countWords(form.content)} words
              </span>
            </div>
            <Textarea
              id="content"
              rows={20}
              disabled={!editable}
              value={form.content}
              onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
              className="font-display text-base leading-relaxed"
              placeholder="Once the rain stopped, the city began to remember…"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="soft"
              disabled={!editable || save.isPending}
              onClick={() => save.mutate()}
            >
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Save draft
            </Button>
            <Button
              variant="hero"
              disabled={!selectedId || !editable || submitForReview.isPending}
              onClick={() => submitForReview.mutate()}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              Submit for review
            </Button>
          </div>
        </div>

        {!selectedId && (stories.data ?? []).length === 0 ? (
          <EmptyState
            className="mt-10"
            icon={FileText}
            title="Every publication starts as a draft"
            description="Write, save, then submit. A moderator reads it before readers do."
          />
        ) : null}
      </section>
    </div>
  );
}
